import { reactive, ref } from 'vue'
import { defineStore } from 'pinia'
import type { RequestScenario, ToastMessage } from '@/types/admin'
import { simulatedFault } from '@/services/faults'

export interface ConfirmOptions {
  title: string
  message: string
  confirmText?: string
  cancelText?: string
  tone?: 'default' | 'danger' | 'primary'
  detail?: string
  requireText?: string
}

export interface ListMemory {
  keyword?: string
  filters?: Record<string, unknown>
  page?: number
  pageSize?: number
  sortField?: string
  sortOrder?: 'asc' | 'desc' | ''
  scrollTop?: number
}


export const useUiStore = defineStore('ui', () => {
  const bootReady = ref(false)
  const booted = ref(false)
  const viewportWidth = ref(typeof window === 'undefined' ? 1440 : window.innerWidth)
  const routeSwitching = ref(false)
  const online = ref(typeof navigator === 'undefined' ? true : navigator.onLine)
  const mobileNavOpen = ref(false)
  const commandOpen = ref(false)
  const notificationsOpen = ref(false)
  const tasksOpen = ref(false)
  const helpOpen = ref(false)
  const rolePreviewOpen = ref(false)
  const toasts = reactive<ToastMessage[]>([])
  const confirmState = reactive<{ open: boolean; options: ConfirmOptions }>({
    open: false,
    options: { title: '', message: '' },
  })
  const dataScopeId = ref('')
  const refreshTick = ref(0)
  const scrollPositions = reactive<Record<string, number>>({})
  const listMemory = reactive<Record<string, ListMemory>>({})
  let confirmResolve: ((value: boolean) => void) | null = null
  let toastSequence = 0

  function toast(type: ToastMessage['type'], title: string, detail = '', duration = 3600): number {
    const id = ++toastSequence
    toasts.push({ id, type, title, detail })
    if (typeof window !== 'undefined') window.setTimeout(() => dismissToast(id), duration)
    return id
  }

  function dismissToast(id: number): void {
    const index = toasts.findIndex(item => item.id === id)
    if (index >= 0) toasts.splice(index, 1)
  }

  function confirm(options: ConfirmOptions): Promise<boolean> {
    confirmState.options = options
    confirmState.open = true
    return new Promise<boolean>(resolve => {
      confirmResolve = resolve
    })
  }

  function answerConfirm(value: boolean): void {
    confirmState.open = false
    confirmResolve?.(value)
    confirmResolve = null
  }

  function markBooted(): void {
    booted.value = true
    bootReady.value = true
  }

  function updateWidth(width: number): void {
    viewportWidth.value = width
    if (typeof document !== 'undefined') {
      document.documentElement.dataset.viewport = width >= 1024 ? 'desktop' : width >= 768 ? 'tablet' : 'mobile'
    }
  }

  function rememberScroll(path: string, top: number): void {
    if (!path) return
    scrollPositions[path] = Math.max(0, Math.round(top))
  }

  function takeScroll(path: string): number {
    return scrollPositions[path] ?? 0
  }

  function scrollFor(path: string): number {
    return takeScroll(path)
  }

  function rememberList(pageId: string, memory: ListMemory): void {
    listMemory[pageId] = {
      ...memory,
      filters: JSON.parse(JSON.stringify(memory.filters ?? {})) as Record<string, unknown>,
      scrollTop: memory.scrollTop ?? listMemory[pageId]?.scrollTop ?? 0,
    }
  }

  function readList(pageId: string): ListMemory | undefined {
    const value = listMemory[pageId]
    if (!value) return undefined
    return { ...value, filters: JSON.parse(JSON.stringify(value.filters ?? {})) as Record<string, unknown> }
  }

  function beginRoute(): void {
    routeSwitching.value = true
    mobileNavOpen.value = false
  }

  function endRoute(): void {
    const reveal = (): void => {
      routeSwitching.value = false
    }
    // Let the target page commit and settle before showing it. This avoids
    // flashing the previous route or an HTML default home page.
    if (typeof requestAnimationFrame === 'undefined') reveal()
    else requestAnimationFrame(() => requestAnimationFrame(reveal))
  }



  function setOnline(value: boolean): void {
    online.value = value
  }

  function triggerRefresh(): void {
    refreshTick.value += 1
  }

  function setScenario(scenario: RequestScenario): void {
    simulatedFault.scenario = scenario
  }

  function setOffline(value: boolean): void {
    simulatedFault.offline = value
    online.value = value ? false : true
  }

  function setLatency(value: number): void {
    simulatedFault.latency = value
  }

  return {
    bootReady,
    booted,
    viewportWidth,
    routeSwitching,
    online,
    mobileNavOpen,
    commandOpen,
    notificationsOpen,
    tasksOpen,
    helpOpen,
    rolePreviewOpen,
    toasts,
    confirmState,
    dataScopeId,
    refreshTick,
    scrollPositions,
    listMemory,
    toast,
    dismissToast,
    confirm,
    answerConfirm,
    markBooted,
    updateWidth,
    rememberScroll,
    takeScroll,
    scrollFor,
    rememberList,
    readList,
    beginRoute,
    endRoute,
    setOnline,
    triggerRefresh,
    setScenario,
    setOffline,
    setLatency,
  }
})
